import { useEffect, useState } from 'react'
import { submitLead, type LeadInput } from '../lib/api'
import { Button } from './Button'
import { X } from 'lucide-react'

export type InquiryType = LeadInput['inquiryType']

interface Props {
  open: boolean
  defaultInquiryType: InquiryType
  onClose: () => void
}

const INQUIRY_OPTIONS: Array<{ value: InquiryType; label: string }> = [
  { value: 'capture_teaming', label: 'Capture / teaming' },
  { value: 'architecture_review', label: 'Architecture review' },
  { value: 'capability_statement', label: 'Capability statement request' },
  { value: 'general', label: 'General inquiry' },
]

export function LeadModal({ open, defaultInquiryType, onClose }: Props) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [organization, setOrganization] = useState('')
  const [role, setRole] = useState('')
  const [inquiryType, setInquiryType] = useState<InquiryType>(defaultInquiryType)
  const [message, setMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<{ msg: string; downloadUrl?: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setInquiryType(defaultInquiryType)
  }, [defaultInquiryType])

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setSuccess(null)
        setError(null)
      }, 300)
      return () => clearTimeout(t)
    }
  }, [open])

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const resp = await submitLead({
      name: name.trim(),
      email: email.trim(),
      organization: organization.trim(),
      role: role.trim(),
      inquiryType,
      message: message.trim(),
      honeypot,
    })
    setSubmitting(false)
    if (resp.success) {
      setSuccess({
        msg: resp.message ?? "Got it — we'll respond within one business day.",
        downloadUrl: resp.downloadUrl,
      })
    } else {
      setError(resp.error ?? 'Something went wrong. Email sales@cylogic.com directly.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-card shadow-card-hover w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 pb-4 border-b border-mist">
          <div>
            <h3 className="text-xl font-semibold text-navy">Talk to CyLogic</h3>
            <p className="text-sm text-navy/65 mt-1">
              We'll respond within one business day.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-navy/50 hover:text-navy p-1 -m-1 rounded hover:bg-mist"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="p-6 space-y-4">
            <div className="bg-foam border border-mint/30 rounded-card p-5">
              <div className="font-semibold text-navy">{success.msg}</div>
              {success.downloadUrl && (
                <div className="mt-3 text-sm text-navy/70">
                  Your capability statement link:{' '}
                  <a
                    href={success.downloadUrl}
                    className="text-mint font-medium hover:underline"
                  >
                    {success.downloadUrl}
                  </a>
                </div>
              )}
            </div>
            <Button onClick={onClose} variant="ghost" className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">
                {error}
              </div>
            )}

            <Field label="Name" required>
              <input
                required
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
              />
            </Field>
            <Field label="Email" required>
              <input
                required
                type="email"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
              />
            </Field>
            <Field label="Organization" required>
              <input
                required
                maxLength={200}
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="form-input"
              />
            </Field>
            <Field label="Role">
              <input
                maxLength={200}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="form-input"
              />
            </Field>
            <Field label="Inquiry type" required>
              <select
                value={inquiryType}
                onChange={(e) => setInquiryType(e.target.value as InquiryType)}
                className="form-input"
              >
                {INQUIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Message">
              <textarea
                rows={4}
                maxLength={5000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="form-input resize-none"
              />
            </Field>

            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              aria-hidden="true"
              className="hidden"
              name="company_url"
            />

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={submitting} className="flex-1" variant="primary">
                {submitting ? 'Sending…' : 'Send'}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>

            <p className="text-xs text-navy/55 mt-2">
              By submitting, you agree CyLogic may email you in response to this inquiry.
            </p>
          </form>
        )}
      </div>
      <style>{`
        .form-input {
          display: block;
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #EDEDF1;
          border-radius: 0.5rem;
          background: white;
          color: #1E2151;
          font-size: 0.875rem;
        }
        .form-input:focus {
          outline: none;
          border-color: #00DFA0;
          box-shadow: 0 0 0 1px #00DFA0;
        }
      `}</style>
    </div>
  )
}

function Field({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-data uppercase tracking-wide text-navy/50 mb-1.5">
        {label} {required && <span className="text-mint">·</span>}
      </label>
      {children}
    </div>
  )
}
